import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, isAdmin } from "@/lib/billing";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  FileText, Wallet, Coins, PieChart as PieIcon, TrendingUp, Activity, Users,
} from "lucide-react";
import { PerWilayahTable } from "@/components/billing/PerWilayahTable";
import { AgingChart } from "@/components/billing/AgingChart";
import { PaymentDrillDialog } from "@/components/billing/PaymentDrillDialog";
import { NbRekapSection } from "@/components/billing/NbRekapSection";

/**
 * Feb 2026 — Dashboard Billing Admin, Tahap 1.
 * ADMIN-ONLY. Konsumen data: `/api/billing/rekap` (summary KPI + donut
 * status/kas) + `/api/billing/dashboard/trend` (combo chart tren bulanan).
 * Billing V1 FROZEN: tidak ada perubahan engine/schema. Dashboard adalah
 * lapisan visualisasi; semua perhitungan reuse Rekap Billing.
 *
 * Scope Tahap 1:
 *  - 4 KPI: Total Tagihan Invoice, Pembayaran atas Tagihan, Penerimaan
 *    Kas, Sisa Tagihan Aktif
 *  - Grafik Tren Bulanan 6/12/YTD/custom (ComposedChart: bar×2 + line×1)
 *  - Komposisi Penerimaan Kas (donut TUNAI/TRANSFER/LOKET)
 *  - Status Invoice (donut LUNAS/SEBAGIAN/BELUM_LUNAS/DIBATALKAN) dgn
 *    count + nominal
 *
 * Deferred Tahap 2: Top 5 Kolektor, Aging, Per-Wilayah, filter kolektor
 * & wilayah, comparison periode sebelumnya.
 */

// ──────────────────────────────────────────────────────────────────
// Helpers
const formatRupiahCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000_000) return `Rp${(v / 1_000_000_000).toFixed(1)}M`;
  if (v >= 1_000_000) return `Rp${(v / 1_000_000).toFixed(1)}jt`;
  if (v >= 1_000) return `Rp${(v / 1_000).toFixed(0)}rb`;
  return `Rp${v}`;
};

// ──────────────────────────────────────────────────────────────────
const MODES = {
  MONTH: { label: "Periode Bulan" },
  CUSTOM: { label: "Rentang Khusus" },
};

// "YYYY-MM" bulan berjalan berdasarkan tanggal lokal WIB.
const wibYm = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit",
}).format(new Date()).slice(0, 7);

const monthRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { dateFrom: `${ym}-01`, dateTo: `${ym}-${String(last).padStart(2, "0")}` };
};

const MONTH_NAMES_LONG = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const ymLongLabel = (ym) => `${MONTH_NAMES_LONG[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}`;

// Bulan YYYY-MM dari rentang tanggal (sama dgn enumerasi Rekap cross-month).
const monthsBetween = (from, to) => {
  const out = [];
  let y = Number(from.slice(0, 4)), m = Number(from.slice(5, 7));
  const ye = Number(to.slice(0, 4)), me = Number(to.slice(5, 7));
  while (y < ye || (y === ye && m <= me)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    if (m === 12) { y += 1; m = 1; } else { m += 1; }
  }
  return out;
};

// Tinggi layar: tall (≥1000px) lega, normal ringkas, short (<850px & desktop) paling ringkas.
const screenMode = () => ({
  tall: window.innerHeight >= 1000,
  short: window.innerHeight < 850 && window.innerWidth >= 1024,
});
const useScreenMode = () => {
  const [mode, setMode] = useState(screenMode);
  useEffect(() => {
    const onResize = () => setMode(screenMode());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mode;
};

function KpiCard({ label, value, hint, Icon, bg, border, textLabel, textValue, testid, onClick, actionHint }) {
  return (
    <button type="button" onClick={onClick} disabled={!onClick} title={actionHint}
      className={`group text-left w-full rounded-lg border p-3 sm:p-4 lg:p-3 ${bg} ${border} transition-[transform,box-shadow] duration-150 enabled:cursor-pointer enabled:hover:-translate-y-0.5 enabled:hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1D4ED8] focus-visible:ring-offset-1 disabled:cursor-default`}
      data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] sm:text-[11px] uppercase tracking-wide font-semibold ${textLabel}`}>
            {label}
          </div>
          <div className={`mt-1 font-mono font-black text-base sm:text-xl break-words ${textValue}`}>
            {value}
          </div>
          {hint ? (
            <div className={`text-[10px] sm:text-[11px] mt-1 leading-snug ${textLabel}`}>{hint}</div>
          ) : null}
        </div>
        {Icon ? <Icon className={`w-5 h-5 sm:w-6 sm:h-6 shrink-0 ${textLabel}`} /> : null}
      </div>
      {actionHint ? (
        <div className={`mt-2 lg:mt-1 text-[10px] font-semibold opacity-70 group-enabled:group-hover:opacity-100 ${textLabel}`}>
          {onClick ? `${actionHint} →` : actionHint}
        </div>
      ) : null}
    </button>
  );
}

export default function BillingDashboardAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drill, setDrill] = useState(null);
  const [mode, setMode] = useState("MONTH");
  const [periodYm, setPeriodYm] = useState(wibYm);
  // Filter mandiri grafik Tren Bulanan — tidak terikat filter utama.
  const [trendYear, setTrendYear] = useState(() => Number(wibYm().slice(0, 4)));
  const [trendLoading, setTrendLoading] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [perKolektor, setPerKolektor] = useState([]);
  const [perWilayah, setPerWilayah] = useState([]);
  const [aging, setAging] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  const admin = isAdmin(user);
  const { tall, short } = useScreenMode();

  // Window filter utama (ringkasan): Periode Bulan atau Rentang Khusus.
  const { dateFrom, dateTo, label } = useMemo(() => {
    if (mode === "CUSTOM" && customFrom && customTo) {
      return { dateFrom: customFrom, dateTo: customTo,
               label: `Rentang Khusus (${customFrom} → ${customTo})` };
    }
    const ym = periodYm || wibYm();
    return { ...monthRange(ym), label: `Periode ${ymLongLabel(ym)}` };
  }, [mode, periodYm, customFrom, customTo]);

  const yearOptions = useMemo(() => {
    const y = Number(wibYm().slice(0, 4));
    return [y, y - 1, y - 2, y - 3, y - 4];
  }, []);

  const load = async () => {
    if (!admin) return;
    setLoading(true);
    try {
      const { data: r } = await api.get("/billing/rekap", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setSummary(r?.summary || null);
      setPerKolektor(Array.isArray(r?.per_kolektor) ? r.per_kolektor : []);
      setPerWilayah(Array.isArray(r?.per_wilayah) ? r.per_wilayah : []);
      const { data: a } = await api.get("/billing/dashboard/aging");
      setAging(a || null);
    } catch (e) {
      toast.error("Gagal memuat Dashboard: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Grafik: selalu Januari–Desember tahun terpilih (endpoint trend existing).
  const loadTrend = async () => {
    if (!admin) return;
    setTrendLoading(true);
    try {
      const { data: t } = await api.get("/billing/dashboard/trend", {
        params: { months: 12, end_month: `${trendYear}-12` },
      });
      setTrend(t || null);
    } catch (e) {
      toast.error("Gagal memuat Tren Bulanan: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "CUSTOM" && (!customFrom || !customTo)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendYear]);

  if (!admin) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Halaman ini hanya untuk Admin.
        </div>
      </div>
    );
  }

  const s = summary || {};
  // Daftar Invoice hanya punya filter 1 periode → navigasi bila rentang 1 bulan.
  const singleYm = dateFrom.slice(0, 7) === dateTo.slice(0, 7) ? dateFrom.slice(0, 7) : null;
  const invoiceNav = (statuses) => (singleYm
    ? () => navigate(`/billing/invoices?periode=${singleYm}&status=${statuses}`)
    : undefined);
  const drillCfg = drill === "KAS" ? {
    title: "Transaksi Penerimaan Kas",
    description: `Pembayaran CONFIRMED dengan tanggal bayar ${dateFrom} s/d ${dateTo}. VOID tidak dihitung.`,
    params: { status: "CONFIRMED", date_from: dateFrom, date_to: dateTo },
    kpiValue: s.total_penerimaan_kas || 0,
  } : drill === "ALOKASI" ? {
    title: "Pembayaran atas Tagihan",
    description: `Pembayaran CONFIRMED (tanggal bayar kapan pun) atas invoice aktif periode tagihan ${monthsBetween(dateFrom, dateTo).join(", ")} — tanpa DIBATALKAN & sumber carry-forward.`,
    params: { status: "CONFIRMED", invoice_periode: monthsBetween(dateFrom, dateTo).join(",") },
    kpiValue: s.total_dibayar || 0,
  } : null;
  const trendItems = (trend?.items || []).map((row) => ({
    ...row, month_label: MONTH_NAMES_LONG[parseInt(row.month.slice(5, 7), 10) - 1].slice(0, 3),
  }));

  const kasData = [
    { name: "TUNAI (Kolektor)", value: s.total_channel_kolektor || 0, color: "#2563EB" },
    { name: "TRANSFER", value: s.total_channel_transfer || 0, color: "#10B981" },
    { name: "LOKET (Kantor)", value: s.total_channel_loket || 0, color: "#F59E0B" },
  ];
  const totalKas = kasData.reduce((n, d) => n + (d.value || 0), 0);

  const statusData = [
    { name: "Lunas",       count: s.count_lunas || 0,        nominal: s.nominal_lunas || 0,        color: "#10B981" },
    { name: "Sebagian",    count: s.count_sebagian || 0,     nominal: s.nominal_sebagian || 0,     color: "#F59E0B" },
    { name: "Belum Lunas", count: s.count_belum_lunas || 0,  nominal: s.nominal_belum_lunas || 0,  color: "#EF4444" },
    { name: "Dibatalkan",  count: s.count_dibatalkan || 0,   nominal: s.nominal_dibatalkan || 0,   color: "#9CA3AF" },
  ];
  const totalStatusCount = statusData.reduce((n, d) => n + (d.count || 0), 0);

  return (
    <div className={`p-4 sm:p-6 lg:py-3 space-y-4 sm:space-y-6 lg:space-y-3 ${short ? "lg:pt-0 lg:pb-2" : ""}`} data-testid="billing-dashboard-root">
      {drillCfg && (
        <PaymentDrillDialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)} {...drillCfg} />
      )}
      {/* Header + filter */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className={`font-black text-2xl ${short ? "" : "sm:text-3xl"} text-[#111827]`}>Dashboard Billing</h1>
          <p className="text-xs sm:text-sm text-[#6B7280]">
            Ringkasan dan analitik data tagihan, pembayaran, dan penerimaan kas. {label}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="dashboard-filter-bar">
          {Object.entries(MODES).map(([key, p]) => (
            <button key={key} type="button" onClick={() => setMode(key)}
              className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold border transition ${
                mode === key
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
              }`}
              data-testid={`dashboard-mode-${key.toLowerCase()}`}>
              {p.label}
            </button>
          ))}
          {mode === "MONTH" && (
            <input type="month" value={periodYm}
                   onChange={(e) => e.target.value && setPeriodYm(e.target.value)}
                   className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs sm:text-sm"
                   data-testid="dashboard-period-month" />
          )}
          {mode === "CUSTOM" && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                     className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"
                     data-testid="dashboard-custom-from" />
              <span className="text-xs text-[#6B7280]">—</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                     className="rounded-md border border-[#E5E7EB] px-2 py-1.5 text-xs"
                     data-testid="dashboard-custom-to" />
            </div>
          )}
        </div>
      </div>

      {/* 4 KPI Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-3 ${short ? "[&>button]:p-2.5" : ""}`}
           data-testid="dashboard-kpi-row">
        <KpiCard label="Total Tagihan Invoice"
          value={loading ? "…" : formatRupiah(s.total_tagihan || 0)}
          hint="Nominal invoice aktif dalam cakupan periode"
          onClick={invoiceNav("LUNAS,SEBAGIAN,BELUM_LUNAS")}
          actionHint={singleYm ? "Lihat daftar invoice" : "Pilih Periode Bulan / rentang 1 bulan untuk melihat daftar"}
          Icon={FileText} bg="bg-[#EFF6FF]" border="border-[#DBEAFE]"
          textLabel="text-[#1D4ED8]" textValue="text-[#1E3A8A]"
          testid="kpi-total-tagihan" />
        <KpiCard label="Pembayaran atas Tagihan"
          value={loading ? "…" : formatRupiah(s.total_dibayar || 0)}
          hint="Pembayaran dialokasikan ke invoice (bukan arus kas)"
          onClick={() => setDrill("ALOKASI")}
          actionHint="Lihat transaksi pembayaran"
          Icon={Wallet} bg="bg-[#ECFDF5]" border="border-[#D1FAE5]"
          textLabel="text-[#047857]" textValue="text-[#065F46]"
          testid="kpi-pembayaran-tagihan" />
        <KpiCard label="Penerimaan Kas"
          value={loading ? "…" : formatRupiah(s.total_penerimaan_kas || 0)}
          hint="Σ pembayaran CONFIRMED, berdasarkan tanggal bayar (termasuk pelunasan tagihan bulan lain)"
          onClick={() => setDrill("KAS")}
          actionHint="Lihat transaksi pembayaran"
          Icon={Coins} bg="bg-[#FFFBEB]" border="border-[#FEF3C7]"
          textLabel="text-[#B45309]" textValue="text-[#92400E]"
          testid="kpi-penerimaan-kas" />
        <KpiCard label="Sisa Tagihan Aktif"
          value={loading ? "…" : formatRupiah(s.total_sisa || 0)}
          hint="Σ sisa invoice aktif (SEBAGIAN + BELUM_LUNAS)"
          onClick={invoiceNav("SEBAGIAN,BELUM_LUNAS")}
          actionHint={singleYm ? "Lihat daftar invoice" : "Pilih Periode Bulan / rentang 1 bulan untuk melihat daftar"}
          Icon={Activity} bg="bg-[#FEF2F2]" border="border-[#FEE2E2]"
          textLabel="text-[#B91C1C]" textValue="text-[#991B1B]"
          testid="kpi-sisa-tagihan" />
      </div>

      {/* Row: Tren Bulanan + Komposisi Kas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-3">
        <div className="lg:col-span-2 rounded-lg border bg-white p-3 sm:p-4 lg:p-3"
             data-testid="dashboard-trend-chart">
          <div className="flex items-center justify-between mb-2 lg:mb-1">
            <h3 className="font-black text-sm sm:text-base text-[#111827] flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Tren Bulanan
            </h3>
            <label className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
              Tahun
              <select value={trendYear} onChange={(e) => setTrendYear(Number(e.target.value))}
                      className="rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-xs font-semibold text-[#111827]"
                      data-testid="dashboard-trend-year">
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              {trendLoading && <span className="text-[10px]">…</span>}
            </label>
          </div>
          <ResponsiveContainer width="100%" height={tall ? 260 : short ? 165 : 220}>
            <ComposedChart data={trendItems} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month_label" tick={{ fontSize: 9 }} interval={0} />
              <YAxis tickFormatter={formatRupiahCompact} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatRupiah(v)}
                       labelFormatter={(l) => `Bulan: ${l} ${trendYear}`}
                       contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="total_tagihan" name="Total Tagihan Invoice" fill="#60A5FA" />
              <Bar dataKey="total_dibayar" name="Pembayaran atas Tagihan" fill="#34D399" />
              <Line type="monotone" dataKey="penerimaan_kas" name="Penerimaan Kas (tgl bayar)"
                    stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="text-[10px] text-[#6B7280] mt-0.5 leading-snug">
            Batang biru &amp; hijau berbasis <b>periode invoice</b>. Garis oranye berbasis <b>tanggal pembayaran</b>.
          </div>
        </div>

        {/* Komposisi Kas */}
        <div className="rounded-lg border bg-white p-3 sm:p-4 lg:p-3" data-testid="dashboard-kas-chart">
          <h3 className="font-black text-sm sm:text-base text-[#111827] flex items-center gap-2 mb-2 lg:mb-1">
            <PieIcon className="w-4 h-4" /> Komposisi Penerimaan Kas
          </h3>
          {totalKas > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={tall ? 150 : short ? 84 : 120}>
                <PieChart>
                  <Pie data={kasData} dataKey="value" nameKey="name"
                       cx="50%" cy="50%" innerRadius={tall ? 42 : short ? 24 : 34} outerRadius={tall ? 68 : short ? 40 : 55} paddingAngle={2}>
                    {kasData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 lg:space-y-1 mt-2 lg:mt-1">
                {kasData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                      <span className="truncate text-[#374151]">{d.name}</span>
                    </div>
                    <div className="text-right shrink-0 flex items-baseline gap-2">
                      <div className="font-mono font-bold">{formatRupiah(d.value)}</div>
                      <div className="text-[10px] w-10 text-[#6B7280]">
                        {totalKas > 0 ? `${((d.value / totalKas) * 100).toFixed(1)}%` : "0%"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-1.5 mt-1.5 border-t text-xs flex items-center justify-between">
                <span className="font-semibold text-[#374151]">Total Kas</span>
                <span className="font-mono font-black text-[#92400E]"
                      data-testid="dashboard-kas-total">{formatRupiah(totalKas)}</span>
              </div>
            </>
          ) : (
            <div className="text-xs text-[#6B7280] py-10 text-center">
              Belum ada penerimaan kas pada rentang ini.
            </div>
          )}
        </div>
      </div>

      {/* Status Invoice (kiri) + Tunggakan Aging (kanan) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-3 items-stretch"
           data-testid="dashboard-status-aging-row">
      <div className="rounded-lg border bg-white p-3 sm:p-4 lg:p-3 h-full flex flex-col" data-testid="dashboard-status-chart">
        <h3 className="font-black text-sm sm:text-base text-[#111827] flex items-center gap-2 mb-2 lg:mb-1">
          <PieIcon className="w-4 h-4" /> Status Invoice
        </h3>
        <div className={`grid grid-cols-1 ${tall ? "sm:grid-cols-[190px_1fr]" : short ? "sm:grid-cols-[130px_1fr]" : "sm:grid-cols-[160px_1fr]"} gap-3 items-center flex-1`}>
          <div className="relative">
            {totalStatusCount > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={tall ? 170 : short ? 112 : 150}>
                  <PieChart>
                    <Pie data={statusData} dataKey="count" nameKey="name"
                         cx="50%" cy="50%" innerRadius={tall ? 48 : short ? 36 : 42} outerRadius={tall ? 76 : short ? 54 : 66} paddingAngle={2}>
                      {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, _, item) => [`${v} invoice`, item.payload.name]}
                             contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-black text-lg text-[#111827]" data-testid="dashboard-status-total">
                    {totalStatusCount.toLocaleString("id-ID")}
                  </span>
                  <span className="text-[10px] text-[#6B7280]">Invoice</span>
                </div>
              </>
            ) : (
              <div className="text-xs text-[#6B7280] py-10 text-center">
                Belum ada invoice pada rentang ini.
              </div>
            )}
          </div>
          <div className="space-y-2 lg:space-y-1">
            {statusData.map((d, i) => {
              const pct = totalStatusCount > 0 ? ((d.count / totalStatusCount) * 100).toFixed(1) : "0.0";
              return (
                <div key={i} className="grid grid-cols-[minmax(84px,1fr)_auto_auto_auto] gap-x-2 items-center text-xs sm:text-sm"
                     data-testid={`dashboard-status-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                    <span className="truncate text-[#374151]">{d.name}</span>
                  </div>
                  <span className="font-mono text-[11px] text-[#6B7280] text-right whitespace-nowrap">{formatRupiah(d.nominal)}</span>
                  <span className="font-black text-[#111827] text-right">{d.count.toLocaleString("id-ID")}</span>
                  <span className="text-[#6B7280] text-right w-11">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-[10px] text-[#6B7280] mt-2 lg:mt-1 leading-snug">
          Invoice DIBATALKAN tidak masuk Total Tagihan Aktif. Invoice sumber carry-forward tidak dihitung ganda.
        </div>
      </div>

      <AgingChart data={aging} loading={loading} compact short
        onOpen={(k) => navigate(`/billing/invoices?aging=${k}${aging?.as_of ? `&aging_as_of=${aging.as_of}` : ""}`)} />
      </div>

      {/* Feb 2026 Stage 2 — Rekap Penerimaan Pembayaran per Kolektor.
          Reuse `per_kolektor` dari /api/billing/rekap. Row "_unassigned"
          selalu di-pin bawah dgn label "Tanpa Kolektor". Billing V1 FROZEN —
          atribusi, VOID, carry-forward, assignment tidak diubah. */}
      <div className="rounded-lg border bg-white p-3 sm:p-4"
           data-testid="dashboard-per-kolektor">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <h3 className="font-black text-sm sm:text-base text-[#111827] flex items-center gap-2">
            <Users className="w-4 h-4" /> Rekap Penerimaan Pembayaran per Kolektor
          </h3>
          <span className="text-[10px] text-[#6B7280]">
            Mengikuti filter periode Dashboard · payment CONFIRMED only
          </span>
        </div>
        {(() => {
          // Pisah "Tanpa Kolektor" ke bawah; sort rest by total_penerimaan_kas desc.
          const rows = perKolektor.slice();
          const unassignedRow = rows.find((r) => r.collector_user_id == null);
          const kolektorRows = rows.filter((r) => r.collector_user_id != null)
                                    .sort((a, b) => (b.total_penerimaan_kas || 0) - (a.total_penerimaan_kas || 0));
          // Total untuk invariant rekonsiliasi (termasuk _unassigned).
          const totalTunai = perKolektor.reduce((n, r) => n + (r.channel_kolektor || 0), 0);
          const totalTransfer = perKolektor.reduce((n, r) => n + (r.channel_transfer || 0), 0);
          const totalLoket = perKolektor.reduce((n, r) => n + (r.channel_loket || 0), 0);
          const totalKas = perKolektor.reduce((n, r) => n + (r.total_penerimaan_kas || 0), 0);
          const totalTx = perKolektor.reduce((n, r) => n + (r.count_tx_total || 0), 0);
          const hasAny = perKolektor.length > 0 && totalKas > 0;
          if (!hasAny) {
            return (
              <div className="text-xs text-[#6B7280] py-10 text-center">
                Belum ada penerimaan pada rentang ini.
              </div>
            );
          }
          const Row = ({ r, idx, unassigned = false }) => (
            <tr className={unassigned ? "bg-[#FAFAFA]" : (idx % 2 === 1 ? "bg-[#F9FAFB]" : "")}
                data-testid={unassigned ? "pk-row-unassigned" : `pk-row-${r.collector_user_id}`}>
              <td className="px-2 sm:px-3 py-2 text-xs text-[#6B7280] whitespace-nowrap">
                {unassigned ? "—" : (idx + 1)}
              </td>
              <td className={`px-2 sm:px-3 py-2 text-xs sm:text-sm ${unassigned ? "text-[#6B7280] italic" : "text-[#111827] font-semibold"}`}>
                {r.kolektor_name || r.kolektor_username || "—"}
              </td>
              <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#1E3A8A] whitespace-nowrap">
                {formatRupiah(r.channel_kolektor || 0)}
                <div className="text-[9px] text-[#6B7280] font-sans">
                  {(r.count_tunai_tx || 0).toLocaleString("id-ID")} tx
                </div>
              </td>
              <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#065F46] whitespace-nowrap">
                {formatRupiah(r.channel_transfer || 0)}
                <div className="text-[9px] text-[#6B7280] font-sans">
                  {(r.count_transfer_tx || 0).toLocaleString("id-ID")} tx
                </div>
              </td>
              <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#92400E] whitespace-nowrap">
                {formatRupiah(r.channel_loket || 0)}
                <div className="text-[9px] text-[#6B7280] font-sans">
                  {(r.count_loket_tx || 0).toLocaleString("id-ID")} tx
                </div>
              </td>
              <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm font-black text-[#111827] whitespace-nowrap">
                {formatRupiah(r.total_penerimaan_kas || 0)}
              </td>
              <td className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm font-semibold text-[#374151] whitespace-nowrap">
                {(r.count_tx_total || 0).toLocaleString("id-ID")}
              </td>
            </tr>
          );
          return (
            <div className="overflow-x-auto -mx-3 sm:-mx-4">
              <table className="min-w-full text-xs sm:text-sm" data-testid="pk-table">
                <thead className="bg-[#F3F4F6] text-[#374151]">
                  <tr>
                    <th className="px-2 sm:px-3 py-2 text-left font-semibold text-[10px] uppercase">#</th>
                    <th className="px-2 sm:px-3 py-2 text-left font-semibold text-[10px] uppercase">Kolektor</th>
                    <th className="px-2 sm:px-3 py-2 text-right font-semibold text-[10px] uppercase">TUNAI</th>
                    <th className="px-2 sm:px-3 py-2 text-right font-semibold text-[10px] uppercase">TRANSFER</th>
                    <th className="px-2 sm:px-3 py-2 text-right font-semibold text-[10px] uppercase">LOKET</th>
                    <th className="px-2 sm:px-3 py-2 text-right font-semibold text-[10px] uppercase">Total Penerimaan</th>
                    <th className="px-2 sm:px-3 py-2 text-right font-semibold text-[10px] uppercase">Transaksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {kolektorRows.map((r, idx) => (
                    <Row key={r.collector_user_id} r={r} idx={idx} />
                  ))}
                  {unassignedRow && (unassignedRow.total_penerimaan_kas > 0 || unassignedRow.count_tx_total > 0) && (
                    <Row r={unassignedRow} idx={-1} unassigned />
                  )}
                  <tr className="bg-[#EFF6FF] font-black" data-testid="pk-row-total">
                    <td className="px-2 sm:px-3 py-2 text-xs"></td>
                    <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-[#1E3A8A]">TOTAL</td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#1E3A8A]">
                      {formatRupiah(totalTunai)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#1E3A8A]">
                      {formatRupiah(totalTransfer)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#1E3A8A]">
                      {formatRupiah(totalLoket)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm text-[#1E3A8A]"
                        data-testid="pk-total-kas">
                      {formatRupiah(totalKas)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 text-right text-xs sm:text-sm text-[#1E3A8A]"
                        data-testid="pk-total-tx">
                      {totalTx.toLocaleString("id-ID")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
        <div className="text-[10px] text-[#6B7280] mt-2 leading-snug">
          Payment CONFIRMED by payment_date. VOID dikecualikan. TUNAI diatribusikan via snapshot kolektor saat payment dicatat; TRANSFER/LOKET via kolektor AKTIF customer saat ini. Transaksi tanpa atribusi tampil sebagai "Tanpa Kolektor". Total tabel ≡ KPI "Penerimaan Kas".
        </div>
      </div>

      <NbRekapSection ym={dateTo.slice(0, 7)} />

      <PerWilayahTable rows={perWilayah} loading={loading} />
    </div>
  );
}
